import Typography from '@mui/material/Typography';
import { FC, ReactElement, useCallback, useEffect, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';

import { ITeamMemberMapped } from '~/utils/clientAllocation';
import { useAddClientAllocationContext } from '~/ui/pages/ActTeam/reusable/ClientAllocationForm/AddClientAllocation/context/AddClientAllocationContext';
import useLocationGroupAllocationsUpdate from '~/ui/pages/ActTeam/reusable/ClientAllocationForm/AddClientAllocation/hooks/updateAllocations/useLocationGroupAllocationsUpdate';
import useAddAllocationAvailability from '~/ui/pages/ActTeam/reusable/ClientAllocationForm/AddClientAllocation/hooks/validateAllocations/useAddAllocationAvailability';
import { IAddAllocationFormValues } from '~/ui/pages/ActTeam/reusable/ClientAllocationForm/AddClientAllocation/types';
import useAllocationConflictMessage from '~/ui/pages/ActTeam/reusable/ClientAllocationForm/shared/hooks/useAllocationConflictMessage';
import AllocationLocationGroupRowItem from '../ClientsRowItem/AllocationLocationGroupRowItem';
import AllocationLocationGroupsSelect from '../SelectFields/AllocationLocationGroupsSelect';

import layoutStyles from '~/ui/pages/ActTeam/reusable/ClientAllocationForm/shared/components/EditAllocationFormLayout/EditAllocationFormLayout.module.scss';

interface IProps {
  teamMemberIdx: number;
  teamMember: ITeamMemberMapped;
}

const AllocationLocationGroupsList: FC<IProps> = ({ teamMemberIdx, teamMember }): ReactElement => {
  const { locationGroups } = useAddClientAllocationContext();
  const { fields, updateLocationGroupAllocations, removeLocationGroupAt, removeClientFromGroup } =
    useLocationGroupAllocationsUpdate(teamMemberIdx);
  const { watch, setValue } = useFormContext<IAddAllocationFormValues>();

  const locationGroupAllocations = watch(
    `teamMembersAllocations.${teamMemberIdx}.locationGroupAllocations` as const,
  );
  const { activeClientIds, clientsAllocations, selectedLocationGroupClientIds } = useMemo(
    () =>
      locationGroupAllocations.reduce(
        (acc, lgAllocation) => {
          const removed = lgAllocation.removedClientIds ?? [];
          const group = locationGroups.find(
            lg => lg.locationGroup.id === lgAllocation.locationGroupId,
          );
          group?.clients.forEach(client => {
            if (!removed.includes(client.id)) {
              acc.activeClientIds.push(client.id);
              acc.clientsAllocations.push({
                clientId: client.id,
                teamId: client.team?.id || client.teams?.[0]?.id,
              });
              acc.selectedLocationGroupClientIds[client.id] = lgAllocation.locationGroupName;
            }
          });
          return acc;
        },
        {
          activeClientIds: [] as number[],
          clientsAllocations: [] as Array<{ clientId: number; teamId: number | undefined }>,
          selectedLocationGroupClientIds: {} as Record<number, string>,
        },
      ),
    [locationGroupAllocations, locationGroups],
  );

  useEffect(() => {
    setValue(
      `teamMembersAllocations.${teamMemberIdx}.selectedLocationGroupClientIds` as const,
      selectedLocationGroupClientIds,
    );
  }, [selectedLocationGroupClientIds, setValue, teamMemberIdx]);

  const { locationGroupListConflict: conflictMessage } = useAllocationConflictMessage({
    teamMemberIdx,
    locationGroups,
  });

  useAddAllocationAvailability({
    clientsAllocations,
    userId: teamMember.id,
  });

  const handleDeleteGroup = useCallback(
    (groupId: number) => {
      const fieldIdx = locationGroupAllocations.findIndex(f => f.locationGroupId === groupId);
      if (fieldIdx === -1) return;

      removeLocationGroupAt(fieldIdx);
    },
    [locationGroupAllocations, removeLocationGroupAt],
  );

  const handleDeleteClient = useCallback(
    (groupId: number, clientId: number) => {
      const fieldIdx = locationGroupAllocations.findIndex(f => f.locationGroupId === groupId);
      if (fieldIdx === -1) return;

      const currentRemoved = locationGroupAllocations[fieldIdx].removedClientIds ?? [];
      const groupTotalClients =
        locationGroups.find(lg => lg.locationGroup.id === groupId)?.clients.length ?? 0;

      if (groupTotalClients - (currentRemoved.length + 1) <= 0) {
        handleDeleteGroup(groupId);
      } else {
        removeClientFromGroup(fieldIdx, clientId);
      }
    },
    [handleDeleteGroup, locationGroupAllocations, locationGroups, removeClientFromGroup],
  );

  return (
    <>
      {conflictMessage && (
        <Typography variant="body2" className={layoutStyles.conflictAlert}>
          {conflictMessage}
        </Typography>
      )}
      <Typography variant="subtitle1" className={layoutStyles.assignedClientsSectionTitle}>
        Location Groups
      </Typography>

      <AllocationLocationGroupsSelect
        teamMemberIdx={teamMemberIdx}
        updateLocationGroupAllocations={updateLocationGroupAllocations}
        activeClientIds={activeClientIds}
        onDeleteClient={handleDeleteClient}
        onDeleteGroup={handleDeleteGroup}
      />

      {fields.map((field, fieldIdx) => (
        <AllocationLocationGroupRowItem
          key={field.id}
          field={field}
          fieldIdx={fieldIdx}
          teamMemberIdx={teamMemberIdx}
          memberId={teamMember.id}
          showGroupName={
            fields.findIndex(item => item.locationGroupId === field.locationGroupId) === fieldIdx
          }
          onRemoveClient={handleDeleteClient}
        />
      ))}
    </>
  );
};

export default AllocationLocationGroupsList;
